import React from "react";

// Import speaker registration tutorial images
import img1 from "../../ui-assets/help/how-to-register-as-speaker/img1.png";
import img2 from "../../ui-assets/help/how-to-register-as-speaker/img2.png";
import img3 from "../../ui-assets/help/how-to-register-as-speaker/img3.png";
import img4 from "../../ui-assets/help/how-to-register-as-speaker/img4.png";
import img5 from "../../ui-assets/help/how-to-register-as-speaker/img5.png";
import img6 from "../../ui-assets/help/how-to-register-as-speaker/img6.png";

const steps = [
  {
    number: "01",
    title: "Open the Event Page",
    text: (
      <>
        Open your preferred event on <strong>Eventfork</strong> and click the
        <strong> Submit a Talk</strong> button to begin your speaker
        application.
      </>
    ),
    image: img1,
    alt: "Event page highlighting the Submit a Talk button",
  },
  {
    number: "02",
    title: "Complete Your Speaker Profile",
    text: (
      <>
        Enter your personal and professional details, including your name,
        professional email, phone number, city, current position, company,
        biography, and profile photo. Complete all required fields.
      </>
    ),
    image: img2,
    alt: "Speaker profile registration form",
  },
  {
    number: "03",
    title: "Add Your Talk Details",
    text: (
      <>
        Enter your talk topic and provide a clear description or abstract
        explaining what attendees will learn. Check the submission
        requirements before continuing.
      </>
    ),
    image: img3,
    alt: "Talk details submission form",
  },
  {
    number: "04",
    title: "Provide Additional Information",
    text: (
      <>
        Select your speaking experience level and optionally add previous
        speaking engagements and professional links, such as LinkedIn,
        GitHub, and other relevant profiles.
      </>
    ),
    image: img4,
    alt: "Additional speaker information form",
  },
  {
    number: "05",
    title: "Review and Submit",
    text: (
      <>
        Review your speaker profile, talk details, and additional information
        carefully. Make sure everything is correct before submitting your
        application.
      </>
    ),
    image: img5,
    alt: "Review and submit speaker application page",
  },
  {
    number: "06",
    title: "Application Submitted",
    text: (
      <>
        Your application has been submitted successfully. Save your
        <strong> Application ID</strong>, check the review timeline, and
        watch your email for updates from the event organizer.
      </>
    ),
    image: img6,
    alt: "Speaker application submission confirmation",
  },
];

function HowToRegisterAsSpeaker({
  helpUrl = "/help",
  homeUrl = "/",
}) {
  return (
    <main className="speaker-help-page">
      <style>{`
        .speaker-help-page {
          --speaker-orange: #ff542e;
          --speaker-orange-light: #fff0eb;
          --speaker-navy: #151d30;
          --speaker-muted: #64718b;
          --speaker-border: #e4e8ef;
          --speaker-green: #16a34a;

          min-height: 100vh;
          background: #f7f7f5;
          color: var(--speaker-navy);
          font-family: inherit;
          padding-bottom: 64px;
        }

        .speaker-help-page *,
        .speaker-help-page *::before,
        .speaker-help-page *::after {
          box-sizing: border-box;
        }

        .speaker-help-hero {
          background: #fff;
          border-bottom: 1px solid var(--speaker-border);
          padding: 32px 24px;
        }

        .speaker-help-hero-inner {
          max-width: 1180px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 24px;
        }

        .speaker-help-breadcrumb {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          color: var(--speaker-muted);
        }

        .speaker-help-breadcrumb a {
          color: var(--speaker-muted);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .speaker-help-breadcrumb a:hover {
          color: var(--speaker-orange);
        }

        .speaker-help-breadcrumb span:last-child {
          color: var(--speaker-orange);
        }

        .speaker-help-hero h1 {
          margin: 0;
          font-size: clamp(26px, 3vw, 36px);
          line-height: 1.2;
          font-weight: 750;
          letter-spacing: -1px;
        }

        .speaker-help-hero p {
          margin: 10px 0 0;
          color: var(--speaker-muted);
          font-size: 15px;
          line-height: 1.7;
        }

        .speaker-help-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
          padding: 11px 16px;
          border-radius: 30px;
          background: var(--speaker-orange-light);
          color: var(--speaker-orange);
          font-size: 13px;
          font-weight: 700;
        }

        .speaker-help-content {
          width: min(1180px, calc(100% - 48px));
          margin: 32px auto 0;
        }

        .speaker-help-note {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px 20px;
          margin: 0 0 36px;
          border: 1px solid #ffddd2;
          border-radius: 12px;
          background: #fffaf8;
          color: #49566d;
          font-size: 14px;
          line-height: 1.8;
        }

        .speaker-help-note-icon {
          flex-shrink: 0;
          color: var(--speaker-orange);
          font-weight: 800;
        }

        .speaker-help-note strong {
          color: var(--speaker-navy);
        }

        .speaker-help-snake {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .speaker-help-step {
          position: relative;
          padding: 0 0 38px 44px;
        }

        .speaker-help-step:not(:last-child)::before {
          content: "";
          position: absolute;
          left: 17px;
          top: 40px;
          bottom: -2px;
          width: 2px;
          background: linear-gradient(
            to bottom,
            var(--speaker-orange),
            #ffd2c4
          );
        }

        .speaker-help-step-marker {
          position: absolute;
          z-index: 2;
          left: 0;
          top: 25px;
          width: 36px;
          height: 36px;
          display: grid;
          place-items: center;
          border: 3px solid #fff;
          border-radius: 50%;
          background: var(--speaker-orange);
          color: #fff;
          font-size: 11px;
          font-weight: 800;
          box-shadow: 0 0 0 3px #ffe4da;
        }

        .speaker-help-card {
          display: grid;
          grid-template-columns: minmax(250px, 0.72fr) minmax(0, 1.28fr);
          gap: 30px;
          align-items: center;
          padding: 26px;
          border: 1px solid var(--speaker-border);
          border-radius: 18px;
          background: #fff;
          box-shadow: 0 5px 22px rgba(20, 30, 50, 0.035);
          transition:
            box-shadow 0.25s ease,
            border-color 0.25s ease,
            transform 0.25s ease;
        }

        .speaker-help-card:hover {
          border-color: #ffd1c2;
          box-shadow: 0 12px 32px rgba(20, 30, 50, 0.07);
          transform: translateY(-2px);
        }

        .speaker-help-copy {
          padding: 4px 0;
        }

        .speaker-help-step-label {
          display: inline-block;
          margin-bottom: 12px;
          color: var(--speaker-orange);
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .speaker-help-copy h2 {
          margin: 0 0 12px;
          color: var(--speaker-navy);
          font-size: clamp(20px, 2vw, 25px);
          line-height: 1.35;
          font-weight: 750;
          letter-spacing: -0.5px;
        }

        .speaker-help-copy h2::after {
          content: "";
          display: block;
          width: 42px;
          height: 4px;
          margin-top: 12px;
          border-radius: 10px;
          background: var(--speaker-orange);
        }

        .speaker-help-copy p {
          margin: 0;
          color: var(--speaker-muted);
          font-size: 14px;
          line-height: 1.9;
        }

        .speaker-help-copy strong {
          color: var(--speaker-navy);
          font-weight: 700;
        }

        .speaker-help-image-wrap {
          min-width: 0;
          margin: 0;
          overflow: hidden;
          border: 1px solid var(--speaker-border);
          border-radius: 12px;
          background: #f5f6f8;
        }

        .speaker-help-image-wrap img {
          display: block;
          width: 100%;
          height: auto;
          max-height: 520px;
          object-fit: contain;
          object-position: center;
        }

        .speaker-help-image-wrap figcaption {
          padding: 12px 16px;
          border-top: 1px solid var(--speaker-border);
          background: #fff;
          color: var(--speaker-muted);
          font-size: 12px;
          font-weight: 600;
          text-align: center;
        }

        .speaker-help-summary {
          display: flex;
          align-items: center;
          gap: 18px;
          margin: 6px 0 0 44px;
          padding: 24px;
          border: 1px solid #ccebd8;
          border-radius: 16px;
          background: #f3fbf6;
        }

        .speaker-help-summary-icon {
          display: grid;
          place-items: center;
          flex-shrink: 0;
          width: 46px;
          height: 46px;
          border-radius: 50%;
          background: var(--speaker-green);
          color: #fff;
          font-size: 21px;
          font-weight: 800;
        }

        .speaker-help-summary-copy {
          flex: 1;
        }

        .speaker-help-summary h2 {
          margin: 0 0 6px;
          color: #166534;
          font-size: 18px;
        }

        .speaker-help-summary p {
          margin: 0;
          color: #4c6b59;
          font-size: 13px;
          line-height: 1.8;
        }

        .speaker-help-back {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          padding: 12px 18px;
          border: 1px solid var(--speaker-border);
          border-radius: 9px;
          background: #fff;
          color: var(--speaker-navy);
          font-size: 13px;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .speaker-help-back:hover {
          border-color: var(--speaker-orange);
          color: var(--speaker-orange);
        }

        .speaker-help-sr {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        @media (max-width: 800px) {
          .speaker-help-card {
            grid-template-columns: 1fr;
            gap: 22px;
            padding: 20px;
          }

          .speaker-help-image-wrap img {
            max-height: none;
          }

          .speaker-help-summary {
            flex-wrap: wrap;
          }

          .speaker-help-back {
            width: 100%;
          }
        }

        @media (max-width: 520px) {
          .speaker-help-hero {
            padding: 24px 18px;
          }

          .speaker-help-hero-inner {
            align-items: flex-start;
            flex-direction: column;
            gap: 18px;
          }

          .speaker-help-hero h1 {
            letter-spacing: -0.6px;
          }

          .speaker-help-content {
            width: calc(100% - 28px);
            margin-top: 22px;
          }

          .speaker-help-note {
            padding: 14px;
            margin-bottom: 28px;
          }

          .speaker-help-step {
            padding-left: 29px;
            padding-bottom: 26px;
          }

          .speaker-help-step:not(:last-child)::before {
            left: 10px;
            top: 31px;
          }

          .speaker-help-step-marker {
            top: 19px;
            width: 23px;
            height: 23px;
            border-width: 2px;
            font-size: 8px;
            box-shadow: 0 0 0 2px #ffe4da;
          }

          .speaker-help-card {
            gap: 16px;
            padding: 16px;
            border-radius: 13px;
          }

          .speaker-help-copy h2 {
            font-size: 20px;
          }

          .speaker-help-copy p {
            font-size: 13px;
            line-height: 1.8;
          }

          .speaker-help-summary {
            gap: 12px;
            margin-left: 29px;
            padding: 17px;
          }

          .speaker-help-summary-icon {
            width: 38px;
            height: 38px;
            font-size: 17px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .speaker-help-card,
          .speaker-help-back,
          .speaker-help-breadcrumb a {
            transition: none;
          }
        }
      `}</style>

      {/* Page header */}
      <header className="speaker-help-hero">
        <div className="speaker-help-hero-inner">
          <div>
            <nav
              className="speaker-help-breadcrumb"
              aria-label="Breadcrumb"
            >
              <a href={homeUrl}>Home</a>
              <span aria-hidden="true">/</span>
              <a href={helpUrl}>Help Center</a>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Register as speaker</span>
            </nav>

            <h1>How to Register as a Speaker</h1>

            <p>
              Follow these six steps to submit your speaker application
              on Eventfork.
            </p>
          </div>

          <span className="speaker-help-chip">
            <span aria-hidden="true">↗</span>
            {steps.length} steps
          </span>
        </div>
      </header>

      <section className="speaker-help-content">
        {/* Introduction */}
        <div className="speaker-help-note">
          <span
            className="speaker-help-note-icon"
            aria-hidden="true"
          >
            ℹ
          </span>

          <span>
            <strong>Before you begin:</strong> Choose an event that accepts
            speaker submissions. Keep your professional details, talk
            abstract, and profile photo ready.
          </span>
        </div>

        {/* Step-by-step instructions */}
        <ol className="speaker-help-snake">
          {steps.map((step) => (
            <li
              key={step.number}
              className="speaker-help-step"
            >
              <span
                className="speaker-help-step-marker"
                aria-hidden="true"
              >
                {step.number}
              </span>

              <article className="speaker-help-card">
                <div className="speaker-help-copy">
                  <span className="speaker-help-step-label">
                    Step {step.number} of 06
                  </span>

                  <h2>
                    <span className="speaker-help-sr">
                      Step {step.number}:{" "}
                    </span>
                    {step.title}
                  </h2>

                  <p>{step.text}</p>
                </div>

                <figure className="speaker-help-image-wrap">
                  <img
                    src={step.image}
                    alt={step.alt}
                    loading={step.number === "01" ? "eager" : "lazy"}
                  />

                  <figcaption>
                    {step.title}
                  </figcaption>
                </figure>
              </article>
            </li>
          ))}
        </ol>

        {/* Completion message */}
        <section className="speaker-help-summary">
          <div
            className="speaker-help-summary-icon"
            aria-hidden="true"
          >
            ✓
          </div>

          <div className="speaker-help-summary-copy">
            <h2>Your speaker application is submitted!</h2>
            <p>
              Keep your application ID for reference and check your email
              for updates from the event organizer. Acceptance depends on
              the event's review process.
            </p>
          </div>

          <a
            className="speaker-help-back"
            href={helpUrl}
          >
            Back to Help Center →
          </a>
        </section>
      </section>
    </main>
  );
}

export default HowToRegisterAsSpeaker;